import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  CheckCircle2,
  MessageCircle,
  Lock,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const SESSION_KEY = "wehelp_feedback_submitted_v1";

function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem("wehelp_session_id");
    if (existing) return existing;
    const sid = crypto.randomUUID();
    sessionStorage.setItem("wehelp_session_id", sid);
    return sid;
  } catch {
    return "anon";
  }
}

interface FeedbackModuleProps {
  interestCategory?: string;
  educationLevel?: string;
  englishLevel?: string;
  selectedRoadmap?: string;
  sourcePage?: string;
  resultsShownAt?: number | null;
}

export const FeedbackModule = ({
  interestCategory,
  educationLevel,
  englishLevel,
  selectedRoadmap,
  sourcePage = "/career-quiz",
  resultsShownAt,
}: FeedbackModuleProps) => {
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [rating, setRating] = useState<"helpful" | "needs_improvement" | "not_sure" | null>(null);
  const [pathwayFound, setPathwayFound] = useState<"yes" | "no" | "not_sure" | null>(null);
  const [userGoal, setUserGoal] = useState<string>("");
  const [missingPathway, setMissingPathway] = useState("");
  const [state, setStateVal] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [optedForFollowup, setOptedForFollowup] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [testimonialPermission, setTestimonialPermission] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const mountedAt = useRef<number>(Date.now());

  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") {
        setAlreadySubmitted(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  if (alreadySubmitted) return null;

  const handleSubmit = async () => {
    if (!rating && !feedbackText.trim() && !pathwayFound) {
      toast.error("Please share a quick rating, pathway answer, or comment.");
      return;
    }
    setSubmitting(true);
    const timeSpent = resultsShownAt
      ? Date.now() - resultsShownAt
      : Date.now() - mountedAt.current;

    const payload = {
      helpful_rating: rating,
      pathway_found: pathwayFound,
      user_goal: userGoal || null,
      missing_pathway: missingPathway.trim() ? missingPathway.trim().slice(0, 500) : null,
      state: state.trim() ? state.trim().slice(0, 60) : null,
      feedback_text: feedbackText.trim() ? feedbackText.trim().slice(0, 2000) : null,
      testimonial_permission: testimonialPermission,
      opted_for_followup: optedForFollowup,
      name: optedForFollowup && name.trim() ? name.trim().slice(0, 120) : null,
      email: optedForFollowup && email.trim() ? email.trim().slice(0, 200) : null,
      whatsapp: optedForFollowup && whatsapp.trim() ? whatsapp.trim().slice(0, 40) : null,
      interest_category: interestCategory ?? null,
      education_level: educationLevel ?? null,
      english_level: englishLevel ?? null,
      selected_roadmap: selectedRoadmap ?? null,
      session_id: getOrCreateSessionId(),
      source_page: sourcePage,
      time_spent_before_feedback_ms: timeSpent,
    };

    try {
      const { error } = await (supabase as any).from("feedback_leads").insert(payload);
      if (error) throw error;
      try {
        await (supabase as any).from("analytics_events").insert({
          event_type: "feedback_submitted",
          interest_category: interestCategory ?? null,
          time_before_click_ms: timeSpent,
          metadata: {
            rating,
            opted_for_followup: optedForFollowup,
            testimonial_permission: testimonialPermission,
            has_text: !!feedbackText.trim(),
          },
        });
      } catch {
        /* analytics silent */
      }
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        /* ignore */
      }
      setSubmitted(true);
    } catch (e) {
      console.warn("feedback insert failed", e);
      toast.error("Could not save feedback. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border border-border/70 bg-card shadow-soft">
      <CardContent className="p-6 md:p-8">
        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div
              key="thanks"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-4"
            >
              <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-3" />
              <h3 className="text-xl font-bold text-foreground mb-2">Thank you.</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Your feedback helps us improve guidance for students across India.
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="max-w-2xl mx-auto"
            >
              <div className="text-center mb-5">
                <Badge
                  variant="outline"
                  className="mb-3 text-[10px] uppercase tracking-wider text-muted-foreground border-muted-foreground/20"
                >
                  Optional · Under 30 seconds
                </Badge>
                <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
                  <MessageCircle className="h-6 w-6 text-primary" />
                  Was this guidance helpful?
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  Your feedback helps improve free education and career guidance for students across India.
                </p>
              </div>

              {/* Quick rating */}
              <div className="flex flex-wrap gap-2 sm:gap-3 justify-center mb-5">
                <button
                  type="button"
                  onClick={() => setRating("helpful")}
                  className={`flex items-center gap-2 px-4 sm:px-5 py-3 rounded-xl border-2 min-h-[52px] font-medium text-sm transition-all ${
                    rating === "helpful"
                      ? "border-primary bg-primary/10 text-primary shadow-soft"
                      : "border-border bg-card text-foreground hover:border-primary/50"
                  }`}
                >
                  <ThumbsUp className="h-4 w-4" />
                  Helpful
                </button>
                <button
                  type="button"
                  onClick={() => setRating("needs_improvement")}
                  className={`flex items-center gap-2 px-4 sm:px-5 py-3 rounded-xl border-2 min-h-[52px] font-medium text-sm transition-all ${
                    rating === "needs_improvement"
                      ? "border-secondary bg-secondary/20 text-foreground shadow-soft"
                      : "border-border bg-card text-foreground hover:border-secondary/50"
                  }`}
                >
                  <ThumbsDown className="h-4 w-4" />
                  Needs Improvement
                </button>
                <button
                  type="button"
                  onClick={() => setRating("not_sure")}
                  className={`flex items-center gap-2 px-4 sm:px-5 py-3 rounded-xl border-2 min-h-[52px] font-medium text-sm transition-all ${
                    rating === "not_sure"
                      ? "border-accent bg-accent/20 text-foreground shadow-soft"
                      : "border-border bg-card text-foreground hover:border-accent/50"
                  }`}
                >
                  <HelpCircle className="h-4 w-4" />
                  Not Sure
                </button>
              </div>

              {/* Pathway found */}
              <div className="mb-5">
                <p className="text-sm font-semibold text-foreground mb-2 text-center">
                  Did you find a useful pathway?
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {([
                    { v: "yes", label: "Yes" },
                    { v: "no", label: "No" },
                    { v: "not_sure", label: "Not Sure" },
                  ] as const).map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setPathwayFound(o.v)}
                      className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                        pathwayFound === o.v
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border bg-card text-foreground hover:border-primary/40"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Optional: user goal + state */}
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">
                    What were you looking for? (optional)
                  </Label>
                  <Select value={userGoal} onValueChange={setUserGoal}>
                    <SelectTrigger aria-label="What were you looking for?">
                      <SelectValue placeholder="Select a goal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="career_guidance">Career Guidance</SelectItem>
                      <SelectItem value="free_courses">Free Courses</SelectItem>
                      <SelectItem value="scholarships">Scholarships</SelectItem>
                      <SelectItem value="job_skills">Job Skills</SelectItem>
                      <SelectItem value="higher_education">Higher Education</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="fb-state" className="text-xs text-muted-foreground mb-1 block">
                    State (optional)
                  </Label>
                  <Input
                    id="fb-state"
                    value={state}
                    onChange={(e) => setStateVal(e.target.value.slice(0, 60))}
                    placeholder="e.g. Maharashtra"
                    maxLength={60}
                  />
                </div>
              </div>

              {/* Optional text */}
              <div className="mb-4">
                <Textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value.slice(0, 2000))}
                  placeholder="Suggestions or feedback (optional)"
                  rows={3}
                  maxLength={2000}
                  className="text-base"
                />
                <p className="text-[11px] text-muted-foreground mt-1 text-right">
                  {feedbackText.length}/2000
                </p>
              </div>

              {/* Missing pathway */}
              <div className="mb-4">
                <Label htmlFor="fb-missing" className="text-xs text-muted-foreground mb-1 block">
                  Missing course or career pathway? (optional)
                </Label>
                <Input
                  id="fb-missing"
                  value={missingPathway}
                  onChange={(e) => setMissingPathway(e.target.value.slice(0, 500))}
                  placeholder="Tell us what's missing"
                  maxLength={500}
                />
              </div>

              {/* Follow-up opt-in */}
              <div className="flex items-start gap-3 mb-3 p-3 rounded-xl bg-muted/40 border border-border/50">
                <Checkbox
                  id="followup"
                  checked={optedForFollowup}
                  onCheckedChange={(v) => setOptedForFollowup(v === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="followup" className="text-sm font-medium text-foreground cursor-pointer leading-snug">
                  I would like guidance or updates about future opportunities
                </Label>
              </div>

              {optedForFollowup && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="grid sm:grid-cols-3 gap-3 mb-3 overflow-hidden"
                >
                  <div>
                    <Label htmlFor="fb-name" className="text-xs text-muted-foreground mb-1 block">
                      Name
                    </Label>
                    <Input
                      id="fb-name"
                      value={name}
                      onChange={(e) => setName(e.target.value.slice(0, 120))}
                      placeholder="Your name"
                      maxLength={120}
                    />
                  </div>
                  <div>
                    <Label htmlFor="fb-email" className="text-xs text-muted-foreground mb-1 block">
                      Email
                    </Label>
                    <Input
                      id="fb-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value.slice(0, 200))}
                      placeholder="you@example.com"
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <Label htmlFor="fb-whatsapp" className="text-xs text-muted-foreground mb-1 block">
                      WhatsApp
                    </Label>
                    <Input
                      id="fb-whatsapp"
                      type="tel"
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.value.slice(0, 40))}
                      placeholder="+91…"
                      maxLength={40}
                    />
                  </div>
                </motion.div>
              )}

              {/* Testimonial permission */}
              <div className="flex items-start gap-3 mb-4 p-3 rounded-xl bg-muted/40 border border-border/50">
                <Checkbox
                  id="testimonial"
                  checked={testimonialPermission}
                  onCheckedChange={(v) => setTestimonialPermission(v === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="testimonial" className="text-sm font-medium text-foreground cursor-pointer leading-snug">
                  You may use my feedback anonymously to improve We-Help.in
                </Label>
              </div>

              {/* Privacy note */}
              <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 mb-5 justify-center text-center">
                <Lock className="h-3 w-3 shrink-0" />
                Contact details are optional and only used if you request follow-up guidance.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || (!rating && !feedbackText.trim())}
                  variant="hero"
                  size="lg"
                  className="w-full sm:w-auto"
                >
                  {submitting ? "Sending…" : "Submit Feedback"}
                </Button>
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => {
                    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
                    setAlreadySubmitted(true);
                  }}
                  className="w-full sm:w-auto text-muted-foreground"
                >
                  Skip
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
};